import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Calendar,
  Tag,
  Phone,
  Quote,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import { POSTS, BLOG_PATH, findPost } from "../data/blog";
import { SERVICES } from "../data/services";
import useDocumentMeta from "../hooks/useDocumentMeta";

const SITE_URL = "https://powerzautoelectrical.com.au";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function ContentBlock({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="bebas text-white text-2xl md:text-3xl tracking-tight mt-12 mb-5 leading-tight">
          {block.text}
        </h2>
      );
    case "ul":
      return (
        <ul className="space-y-3 my-6 ml-1">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-white/85 barlow text-base md:text-lg leading-relaxed"
            >
              <span className="text-cyan-pz mt-1.5 shrink-0">▸</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-3 my-6 ml-1 list-decimal list-inside">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="text-white/85 barlow text-base md:text-lg leading-relaxed"
            >
              {item}
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <blockquote className="my-8 border-l-4 border-cyan-pz bg-cyan-pz/5 pl-5 md:pl-7 pr-4 py-5 relative">
          <Quote className="absolute -top-3 -left-3 w-6 h-6 text-cyan-pz/60 bg-black p-1" />
          <p className="text-cyan-pz/95 barlow text-lg md:text-xl italic leading-relaxed">
            {block.text}
          </p>
        </blockquote>
      );
    case "cta":
      return (
        <div className="mt-10 mb-2 p-6 md:p-8 border border-cyan-pz/40 bg-cyan-pz/5 clip-perf">
          <p className="text-white barlow text-base md:text-lg leading-relaxed mb-5">
            {block.text}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="blog-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-cyan-pz text-black px-6 py-3 bebas tracking-widest hover:bg-white transition-colors clip-perf"
            >
              <Phone className="w-4 h-4" />
              CALL {SITE.business.phone}
            </a>
            <a
              href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20Enquiry`}
              data-testid="blog-cta-email"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-6 py-3 bebas tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              EMAIL US
            </a>
          </div>
        </div>
      );
    case "p":
    default:
      return (
        <p className="text-white/85 barlow text-base md:text-lg leading-relaxed my-5">
          {block.text}
        </p>
      );
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  // useDocumentMeta must always be called, even when post is missing,
  // so we set safe defaults and short-circuit render below.
  const canonical = post ? `${SITE_URL}${BLOG_PATH(post.slug)}` : `${SITE_URL}/blog`;

  useDocumentMeta({
    title: post ? post.metaTitle : "Blog | Powerz Auto Electrical Sunshine Coast",
    description: post ? post.metaDesc : "Powerz Auto Electrical blog.",
    canonical,
    image: post ? `${SITE_URL}${post.image}` : SITE.business.logo,
    keywords: post ? post.keywords.join(", ") : undefined,
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.metaDesc,
            datePublished: post.publishedAt,
            dateModified: post.publishedAt,
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            image: `${SITE_URL}${post.image}`,
            author: {
              "@type": "Organization",
              name: SITE.business.name,
              url: SITE_URL,
            },
            publisher: {
              "@type": "Organization",
              name: SITE.business.name,
              logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}${SITE.business.logo}`,
              },
            },
            keywords: post.keywords.join(", "),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: canonical },
            ],
          },
        ]
      : undefined,
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Suggest 3 other posts for "Keep reading"
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  // Reverse map: services that declared this post in their relatedBlogs
  const relatedServices = SERVICES.filter(
    (s) => Array.isArray(s.relatedBlogs) && s.relatedBlogs.includes(post.slug)
  ).slice(0, 3);

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <article>
        <section className="relative bg-black pt-36 md:pt-44 pb-10 md:pb-12">
          <div className="max-w-4xl mx-auto px-5 lg:px-8">
            <Link
              to="/blog"
              data-testid="blogpost-back-link"
              className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              BACK TO BLOG
            </Link>
            <div className="flex flex-wrap items-center gap-3 text-white/50 barlow text-xs md:text-sm mb-5">
              <span className="inline-flex items-center gap-1.5 text-cyan-pz">
                <Tag className="w-3.5 h-3.5" />
                {post.category}
              </span>
              <span className="text-white/20">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.publishedAt)}
              </span>
              <span className="text-white/20">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.readTime}
              </span>
            </div>
            <h1
              data-testid="blogpost-headline"
              className="bebas text-white text-4xl md:text-6xl leading-[0.95] tracking-tight"
            >
              {post.title}
            </h1>
            <p className="mt-6 text-white/70 barlow text-base md:text-xl leading-relaxed border-l-2 border-cyan-pz/40 pl-5">
              {post.excerpt}
            </p>
          </div>
        </section>

        {/* Hero image */}
        <section className="relative bg-black pb-12">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="relative aspect-[16/9] overflow-hidden border border-white/10">
              <img
                src={post.image}
                alt={post.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="relative bg-black pb-20 md:pb-28">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            {post.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}
          </div>
        </section>
      </article>

      {/* Related SERVICE pages — internal-link cluster back into the money pages */}
      {relatedServices.length > 0 && (
        <section
          data-testid="blogpost-related-services"
          className="relative bg-black border-t border-white/5 py-16"
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                RELATED SERVICES
              </span>
            </div>
            <h2 className="bebas text-white text-2xl md:text-4xl leading-[0.95] mb-8">
              NEED A HAND WITH THIS? <br />
              <span className="text-cyan-pz">HERE'S WHAT WE OFFER.</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`blogpost-related-service-${s.slug}`}
                  className="bg-black px-6 py-5 hover:bg-cyan-pz/10 transition-colors group"
                >
                  <p className="bebas text-cyan-pz/60 tracking-widest text-xs">
                    {s.kicker ? s.kicker.toUpperCase() : "SUNSHINE COAST"}
                  </p>
                  <p className="bebas text-white tracking-wider text-xl group-hover:text-cyan-pz transition-colors mt-1">
                    {s.name.toUpperCase()}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-3 bebas text-cyan-pz tracking-[0.2em] text-xs">
                    VIEW SERVICE
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related posts */}
      <section className="relative bg-[#0A0A0A] border-t border-white/5 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              KEEP READING
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-4xl leading-[1] tracking-tight mb-10">
            MORE FROM THE WORKSHOP
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p, idx) => (
              <Link
                key={p.slug}
                to={BLOG_PATH(p.slug)}
                data-testid={`blogpost-related-${idx}`}
                className="group flex flex-col bg-black border border-white/10 overflow-hidden clip-perf hover:border-cyan-pz/50 transition-colors"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <p className="bebas text-cyan-pz/80 tracking-widest text-[10px] mb-2">
                    {p.category.toUpperCase()} · {p.readTime}
                  </p>
                  <h3 className="bebas text-white text-lg md:text-xl leading-[1.05] tracking-tight group-hover:text-cyan-pz transition-colors mb-3">
                    {p.title}
                  </h3>
                  <span className="inline-flex items-center gap-2 text-cyan-pz bebas tracking-widest text-xs">
                    READ
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/blog"
              data-testid="blogpost-back-to-blog"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-7 py-4 bebas tracking-widest hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              ALL BLOG POSTS
            </Link>
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
